"use client";

import { useQuery } from "@tanstack/react-query";
import get from "lodash/get";
import { useParams, useSearchParams } from "next/navigation";
import { useEffect } from "react";
import WaitScreenContent from "@/app/[lng]/auth/_components/wait-screen-content/wait-screen-content";
import { REALMS } from "@/constants/process";
import useChangeRealm from "@/hooks/use-change-realm";
import useCurrentUserManager from "@/hooks/use-current-user-manager";
import useHandleApiError from "@/hooks/use-handle-api-error";
import { useLocalizedRouter } from "@/hooks/use-localized-router";
import type { Environment } from "@/repositories/process/types";
import ProcessRepository from "@/repositories/process/process-repository";
import usePersistedBoundStore from "@/store/persisted-bound-store";
import type { RealmSliceState } from "@/store/realm-slice";
import { isAuthenticated as _isAuthenticated, shouldChangeRealm } from "@/utils/auth";
import { saveTabEnvironment, setControlledRealmSwitchSignout } from "@/utils/tab-session";

export default function Process() {
  const changeRealm = useChangeRealm();
  const currentUserManager = useCurrentUserManager();
  const handleApiError = useHandleApiError();
  const params = useParams<{ processUrl: string }>();
  const processUrl = params?.processUrl ?? "";
  const realm = usePersistedBoundStore.use.realm();
  const { navigateTo } = useLocalizedRouter();
  const searchParams = useSearchParams();
  const setEnvironment = usePersistedBoundStore.use.setEnvironment();
  const setEnvironmentUrl = usePersistedBoundStore.use.setEnvironmentUrl();

  const processRepository = new ProcessRepository();

  const token = searchParams?.get("token") ?? "";
  const searchParamsString = searchParams?.toString() ?? "";

  const { data } = useQuery({
    queryKey: ["getProcessEnvironment", processUrl],
    queryFn: () => processRepository.getProcessEnvironment(processUrl),
    retry: (failureCount, error) => {
      const { status } = handleApiError(error);
      if (status === 404 || failureCount === 2) {
        handleApiError(error, true);
        return false;
      }
      return true;
    },
  });

  useEffect(() => {
    if (data) {
      if (!currentUserManager) {
        return;
      }

      const processId = get(data, "data.id");
      const environment = get(data, "data") as Environment;

      // Save to store (localStorage) and sessionStorage (per-tab)
      setEnvironment(environment);
      setEnvironmentUrl(processUrl);
      saveTabEnvironment(environment, processUrl);

      const environmentTypeName = environment.environmentType?.Name;
      const fullProfile = environment.fullProfile;

      const isControlled = environmentTypeName === "CONTROLLED";
      const isControlledStandard = isControlled && fullProfile;
      const isControlledBasic = isControlled && !fullProfile;
      const isAutologin = isControlledBasic && token;
      let pendingProcessTarget = processUrl;
      if (searchParamsString) {
        pendingProcessTarget = `${processUrl}?${searchParamsString}`;
      }

      (async () => {
        if (
          isControlledStandard &&
          shouldChangeRealm(realm, REALMS.EVCORE as RealmSliceState["realm"])
        ) {
          setControlledRealmSwitchSignout(pendingProcessTarget);
          await changeRealm(REALMS.EVCORE as RealmSliceState["realm"]);
        }
        if (
          isControlledBasic &&
          shouldChangeRealm(realm, REALMS.EVBASIC as RealmSliceState["realm"])
        ) {
          setControlledRealmSwitchSignout(pendingProcessTarget);
          await changeRealm(REALMS.EVBASIC as RealmSliceState["realm"]);
        }

        const isAuthenticated = await _isAuthenticated(currentUserManager);

        // Check if this tab needs a fresh login (set by clearSessionAndRedirect when
        // accessing a process the user doesn't belong to)
        const needsFreshLogin = sessionStorage.getItem("needs_fresh_login") === "true";
        if (needsFreshLogin) {
          sessionStorage.removeItem("needs_fresh_login");
        }

        const effectivelyAuthenticated = isAuthenticated && !needsFreshLogin;

        if (isControlledStandard && effectivelyAuthenticated) {
          navigateTo(`/process/${processId}`);
          return;
        }

        if (isControlledStandard && !effectivelyAuthenticated) {
          navigateTo("/auth/login/controlled/standard");
          return;
        }

        if (isAutologin) {
          navigateTo(`/auth/autologin?token=${token}`);
          return;
        }

        if (isControlledBasic && effectivelyAuthenticated) {
          navigateTo(`/process/${processId}`);
          return;
        }

        if (isControlledBasic && !effectivelyAuthenticated) {
          navigateTo("/auth/login/controlled/basic");
        }
      })();
    }
  }, [
    changeRealm,
    currentUserManager,
    data,
    processUrl,
    realm,
    navigateTo,
    setEnvironment,
    setEnvironmentUrl,
    token,
    searchParamsString,
  ]);

  return (
    <div className="h-full flex flex-col justify-center items-center space-y-5 text-center">
      <WaitScreenContent />
    </div>
  );
}
